import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useFormContext } from "react-hook-form";
import { useDrag, useDrop } from "react-dnd";
import {
  DragIndicator,
  KeyboardArrowDown,
  KeyboardArrowUp,
  FormatListBulleted,
} from "@mui/icons-material";
import {
  DialogTypes,
  ItemTypes,
  TYPE_FILE_UPLOAD,
  ResultType,
} from "@app/constants";
import {
  CircularProgress,
  TextField,
  AccordionSummary,
  Typography,
  AccordionDetails,
} from "@mui/material";
import { IconDelete, IconInputError } from "@app/components/icons";
import { useTranslation } from "react-i18next";
import { ConfirmDialog } from "@app/pages/Features";
import { Button } from "@app/components/atoms";
import { REGEX, isMobileDevice, safeSliceGraphemes } from "@app/helpers";
import { InspectionItem } from "@app/types";
import { setShowAlert, store } from "@app/store";
import type { Identifier } from "dnd-core";
import { format } from "date-fns/format";
import { IconSelectedNewSize } from "@app/components/icons/IconSelected/IconSelectedNew";
import "@app/pages/Features/GroupList/GroupDetail/Components/InspectionForm/Card.css";
import {
  IconMaintenaceNewSize,
  IconNotApplicableDash,
} from "@app/components/icons/IconMaintenace/IconMaintenanceNew";
import { IconMessage } from "@app/components/icons/IconMessage/IconMessage";
import ImageIcon from "@mui/icons-material/Image";
import Accordion from "@mui/material/Accordion";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { MediaUpload } from "./MediaUpload/MediaUpload";
import {
  PredefinedCommentsDialog,
  MAX_PREDEFINED_COMMENTS,
  type PredefinedCommentOption,
} from "./PredefinedCommentsDialog";

export interface CardProps {
  id: string | number;
  index: number;
  card: InspectionItem;
  moveCard: (dragIndex: number, hoverIndex: number) => void;
  deleteCard: (index: number) => void;
  updateCard: (index: number, object: Record<string, unknown>) => void;
  setValue: any;
  mediaUploadErrorMsg: string;
  setMediaTransferred: (isTransferred: boolean) => void;
}

interface DragItem {
  id: string;
  index: number;
}

const switchToResultType: Record<number, string> = {
  1: "OK_OR_ANOMARY",
  2: "OK_ANOMARY_NOT_APPLICABLE",
  3: "NONE",
  4: "FREE_TEXT",
};

const resultTypeToSwitch = {
  [ResultType.OK_OR_ANOMARY]: 1,
  [ResultType.OK_ANOMARY_NOT_APPLICABLE]: 2,
  [ResultType.NONE]: 3,
  [ResultType.FREE_TEXT]: 4,
};

export const Card = memo(
  forwardRef(function Card(
    {
      id,
      card,
      moveCard,
      index,
      deleteCard,
      updateCard,
      setValue,
      mediaUploadErrorMsg,
      setMediaTransferred,
    }: CardProps,
    ref: React.Ref<{ cancel: () => void }>,
  ) {
    const { t } = useTranslation();
    const originalIndex = index;

    const [localName, setLocalName] = useState(card.name);
    const [localDescription, setLocalDescription] = useState(card.description);
    const [prevCardName, setPrevCardName] = useState(card.previousName);
    const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
    const [openPredefinedDialog, setOpenPredefinedDialog] = useState(false);

    const [mediaList, setMediaList] = useState<File[]>([]);
    const [isMediaLoading, setIsMediaLoading] = useState(true);
    const [, setMediaLoaded] = useState(false);
    const hasTransferredRef = useRef(false);
    const [isInspectionItemNameInputFocus, setIsInspectionItemNameInputFocus] =
      useState(false);
    const [, setIsInspectionItemDescriptionInputFocus] = useState(false);

    const [activeSwitch, setActiveSwitch] = useState<number>(() => {
      return (
        resultTypeToSwitch[
          card.resultType as keyof typeof resultTypeToSwitch
        ] || 1
      );
    });

    const {
      register,
      watch,
      trigger,
      formState: { errors },
    } = useFormContext<{
      inspectionItems: InspectionItem[];
    }>();

    const previousName = watch(`inspectionItems.${originalIndex}.previousName`);
    const inspectionItemIsOpen = watch(
      `inspectionItems.${originalIndex}.isOpen`,
    );
    const inspectionItemNameErrors =
      errors?.inspectionItems?.[originalIndex]?.name;
    const inspectionItemDescriptionErrors =
      errors?.inspectionItems?.[originalIndex]?.description;

    const abortControllersRef = useRef<AbortController[]>([]);
    const cardRef = useRef(card);

    useEffect(() => {
      cardRef.current = card;
    }, [card]);

    useEffect(() => {
      setLocalName(card.name);
      setLocalDescription(card.description);
    }, [card.name, card.description]);

    const confirmProps = {
      typeDialog: DialogTypes.ERROR,
      title: "titleDeleteCard",
      content: "desDeleteCard",
      btnSubmitText: "formAction.delete",
      onCancel() {
        setIsOpenDeleteDialog(false);
      },
      onSubmit() {
        handleDeleteCard();
        setIsOpenDeleteDialog(false);
      },
      classBtnSubmit: "btn-primary bg-red",
    };

    const uploadDescription = [
      `${t("dialogReport.pleaseUploadFile")}`,
      `<span class="fs-12">${t("dialogReport.thatCanBeUploadedAre")}</span>`,
      `<span class="fs-12">${t("dialogReport.fileFormats")}</span>`,
      `<span class="fs-12">${t("dialogReport.videosCanBeUploaded")}</span>`,
    ];

    const options = [
      {
        id: 1,
        icons: [
          <IconMaintenaceNewSize fillColor={"#DA9309"} iconColor={"white"} />,
          <IconSelectedNewSize fillColor={"#0e7c47"} iconColor={"white"} />,
        ],
        label: t("ANOMARY"),
      },
      {
        id: 2,
        icons: [
          <IconNotApplicableDash />,
          <IconMaintenaceNewSize fillColor={"#DA9309"} iconColor={"white"} />,
          <IconSelectedNewSize fillColor={"#0e7c47"} iconColor={"white"} />,
        ],
        label: t("ANOMARYNOTAPP"),
      },
      {
        id: 3,
        icons: [<IconMessage />, <ImageIcon />],
        label: t("CommentFile"),
      },
    ];

    const predefinedCommentCount = card.options?.length ?? 0;
    const hasPredefinedComments = predefinedCommentCount > 0;

    const [, drag] = useDrag({
      type: ItemTypes.CARD,
      item: { id, index },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    const [, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
      accept: ItemTypes.CARD,
      collect(monitor) {
        return {
          handlerId: monitor.getHandlerId(),
        };
      },
      hover(item: DragItem) {
        const dragIndex = item.index;
        const hoverIndex = index;
        moveCard(dragIndex, hoverIndex);
        item.index = hoverIndex;
      },
    });

    const handleDeleteCard = () => {
      store.dispatch(setShowAlert(t("alreadyDeleted")));
      deleteCard(originalIndex);
    };

    const loadMediaFiles = async () => {
      if (mediaList.length > 0) return;

      setIsMediaLoading(true);
      const files: File[] = [];

      try {
        if (
          Array.isArray(card.customInspectionItemMedias) &&
          card.customInspectionItemMedias.length > 0
        ) {
          const processedFiles = card.customInspectionItemMedias.map(
            (media) => {
              Object.defineProperty(media, "lastModifiedFormatted", {
                value: format(
                  new Date(media.captureMediaAt || Date.now()),
                  "yyyy-MM-dd HH:mm:ss",
                ),
                writable: false,
              });
              return media;
            },
          );

          setMediaList(processedFiles);
          setIsMediaLoading(false);
          setMediaTransferred(true);
          return;
        }

        if (
          Array.isArray(card.itemSampleMedia) &&
          card.itemSampleMedia.length > 0
        ) {
          const controller = new AbortController();
          abortControllersRef.current.push(controller);
          const fetchPromises = card.itemSampleMedia.map(async (media) => {
            const response = await fetch(media.sampleMediaUrl, {
              signal: controller.signal,
            });
            const blob = await response.blob();

            const fileName =
              media.sampleMediaUrl.split("/").pop()?.split("?")[0] ||
              "sample-image.jpg";
            const file = new File([blob], fileName, { type: blob.type });

            Object.defineProperty(file, "lastModifiedFormatted", {
              value: format(
                new Date(file.lastModified || Date.now()),
                "yyyy-MM-dd HH:mm:ss",
              ),
              writable: false,
            });

            return file;
          });

          const fetchedFiles = await Promise.all(fetchPromises);
          files.push(...fetchedFiles);

          updateCard(originalIndex, {
            ...cardRef.current,
            customInspectionItemMedias: fetchedFiles,
            itemSampleMedia: [],
          });

          setMediaList(fetchedFiles);
        }

        setMediaTransferred(true);
      } catch (error: any) {
        if (error.name === "AbortError") {
        } else {
          console.error("Error loading media", error);
        }
      } finally {
        setIsMediaLoading(false);
        setMediaLoaded(true);
      }
    };

    const handleMediaUpdate = (files: File[]) => {
      const updatedFiles = files.filter(
        (file) =>
          !card.itemSampleMedia?.some((sample) =>
            sample.sampleMediaUrl.includes(file.name),
          ),
      );

      updateCard(originalIndex, {
        ...card,
        isOpen: inspectionItemIsOpen,
        customInspectionItemMedias: updatedFiles,
        itemSampleMedia: card.itemSampleMedia?.filter(
          (sample) =>
            !files.some((file) => sample.sampleMediaUrl.includes(file.name)),
        ),
        name: localName.trim(),
        description: localDescription.trim(),
      });

      setMediaList(updatedFiles);
    };

    const handleSwitchChange = useCallback(
      (switchNumber: number) => {
        setActiveSwitch(switchNumber);
        const nextResultType =
          switchToResultType[switchNumber as keyof typeof switchToResultType];
        const preservedOptions = card.options ?? [];
        const nextCard = {
          ...card,
          isOpen: inspectionItemIsOpen,
          name: localName.trim(),
          description: localDescription.trim(),
          customInspectionItemMedias: card.customInspectionItemMedias,
          resultType: nextResultType,
          options: preservedOptions,
        };

        updateCard(originalIndex, nextCard);
        setValue(
          `inspectionItems.${originalIndex}.resultType`,
          nextResultType,
          {
            shouldValidate: true,
          },
        );
        setValue(`inspectionItems.${originalIndex}.options`, preservedOptions, {
          shouldValidate: nextResultType === ResultType.FREE_TEXT,
        });

        if (nextResultType === ResultType.FREE_TEXT) {
          trigger(`inspectionItems.${originalIndex}.options`);
        }
      },
      [
        card,
        inspectionItemIsOpen,
        localName,
        localDescription,
        originalIndex,
        setValue,
        trigger,
        updateCard,
      ],
    );

    const handleSavePredefinedComments = useCallback(
      (options: PredefinedCommentOption[]) => {
        const nextCard = {
          ...card,
          isOpen: inspectionItemIsOpen,
          name: localName.trim(),
          description: localDescription.trim(),
          customInspectionItemMedias: card.customInspectionItemMedias,
          options,
        };

        updateCard(originalIndex, nextCard);
        setValue(`inspectionItems.${originalIndex}.options`, options, {
          shouldValidate: activeSwitch === 4,
        });

        if (activeSwitch === 4) {
          trigger(`inspectionItems.${originalIndex}.options`);
        }
      },
      [
        activeSwitch,
        card,
        inspectionItemIsOpen,
        localDescription,
        localName,
        originalIndex,
        setValue,
        trigger,
        updateCard,
      ],
    );

    useEffect(() => {
      handleSwitchChange(activeSwitch);
    }, []);

    useEffect(() => {
      setPrevCardName(previousName);
    }, [previousName]);

    useEffect(() => {
      if (!isMediaLoading && !hasTransferredRef.current) {
        setMediaTransferred(true);
        hasTransferredRef.current = true;
      }
    }, [isMediaLoading, setMediaTransferred]);

    useEffect(() => {
      if (isMediaLoading) {
        hasTransferredRef.current = false;
      }
    }, [isMediaLoading]);

    useImperativeHandle(ref, () => ({
      cancel: () => {
        abortControllersRef.current.forEach((ctrl) => ctrl.abort());
        abortControllersRef.current = [];
      },
    }));

    useEffect(() => {
      const newSwitch =
        resultTypeToSwitch[card.resultType as keyof typeof resultTypeToSwitch];
      if (newSwitch && newSwitch !== activeSwitch) {
        setActiveSwitch(newSwitch);
      }
    }, [card.resultType, activeSwitch]);

    useEffect(() => {
      if (
        (!Array.isArray(card.customInspectionItemMedias) ||
          card.customInspectionItemMedias.length === 0) &&
        Array.isArray(card.itemSampleMedia) &&
        card.itemSampleMedia.length > 0
      ) {
        loadMediaFiles();
      }
    }, []);

    useEffect(() => {
      loadMediaFiles();
    }, [card.inspectionItemId]);

    useEffect(() => {
      register(`inspectionItems.${originalIndex}.name`, {
        validate: {
          hasEmoji: (value) => REGEX.emojiRegex.test(value),
          hasInvalidChar: (value) => REGEX.commonNameFieldRegex.test(value),
          required: (value) => !!value.trim(),
          maxGraphemes: (value) => {
            const segmenter = new Intl.Segmenter(undefined, {
              granularity: "grapheme",
            });
            return [...segmenter.segment(value)].length <= 100;
          },
        },
      });

      register(`inspectionItems.${originalIndex}.description`, {
        validate: {
          hasEmoji: (value) => REGEX.emojiRegex.test(value),
          hasInvalidChar: (value) =>
            REGEX.commonNameFieldCharacterMuiltiLineRegex.test(value),
          maxGraphemes: (value) => {
            const segmenter = new Intl.Segmenter(undefined, {
              granularity: "grapheme",
            });
            return [...segmenter.segment(value)].length <= 256;
          },
        },
      });

      register(`inspectionItems.${originalIndex}.options`, {
        validate: (value, formValues) => {
          const resultType =
            formValues.inspectionItems?.[originalIndex]?.resultType;
          if (resultType !== ResultType.FREE_TEXT) {
            return true;
          }

          return (
            Array.isArray(value) &&
            value.length > 0 &&
            value.every((option) => !!option?.name?.trim())
          );
        },
      });
    }, [register, originalIndex]);

    const syncNameWithForm = useCallback(() => {
      const trimmedName = localName.trim();

      setValue(`inspectionItems.${originalIndex}.name`, trimmedName, {
        shouldValidate: true,
      });

      trigger(`inspectionItems.${originalIndex}.name`);

      if (!inspectionItemNameErrors && trimmedName) {
        setPrevCardName(trimmedName);
        setValue(`inspectionItems.${originalIndex}.previousName`, trimmedName);
      }
    }, [localName, originalIndex, setValue, trigger, inspectionItemNameErrors]);

    const syncDescriptionWithForm = useCallback(() => {
      const trimmedDescription = localDescription.trim();

      setValue(
        `inspectionItems.${originalIndex}.description`,
        trimmedDescription,
        { shouldValidate: true },
      );

      trigger(`inspectionItems.${originalIndex}.description`);
    }, [localDescription, originalIndex, setValue, trigger]);

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const safeValue = safeSliceGraphemes(e.target.value, 100);
      setLocalName(safeValue);
    };

    const handleDescriptionChange = (
      e: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const safeValue = safeSliceGraphemes(e.target.value, 256);
      setLocalDescription(safeValue);
    };

    const handleNameBlur = () => {
      setIsInspectionItemNameInputFocus(false);
      syncNameWithForm();
    };

    const handleDescriptionBlur = () => {
      setIsInspectionItemDescriptionInputFocus(false);
      syncDescriptionWithForm();
    };

    const handleNameFocus = () => {
      if (!inspectionItemNameErrors || !localName) {
        setPrevCardName(localName);
        setValue(`inspectionItems.${originalIndex}.previousName`, localName);
      }
      setIsInspectionItemNameInputFocus(true);
    };

    const handleDescriptionFocus = () => {
      setIsInspectionItemDescriptionInputFocus(true);
    };

    return (
      <li
        className="card-item card-item-draggable bg-gray"
        ref={(node) => {
          if (isMobileDevice || inspectionItemIsOpen) {
            return;
          }
          drag(drop(node));
        }}
      >
        <div
          className="card-item-title flex pointer"
          onClick={() =>
            setValue(
              `inspectionItems.${originalIndex}.isOpen`,
              !inspectionItemIsOpen,
            )
          }
        >
          <div
            ref={(node) => {
              if (inspectionItemIsOpen) {
                return;
              }
              drag(drop(node));
            }}
            className="wrapper-drap-icon flex align-items-center"
          >
            <DragIndicator className="drag-icon" />
          </div>
          <div className="flex align-items-center justify-content-between w-100 wrapper-title-card">
            <span className="txt-card txt-pre-wrap word-break">
              {!inspectionItemNameErrors?.type &&
              !isInspectionItemNameInputFocus
                ? localName
                : prevCardName}
            </span>
            {inspectionItemIsOpen ? (
              <KeyboardArrowUp className="arrow-icon ml-10" />
            ) : (
              <KeyboardArrowDown className="arrow-icon ml-10" />
            )}
          </div>
        </div>
        <div
          className={`card-item-content ${inspectionItemIsOpen && "d-block"}`}
        >
          <div className="wrapper-text-field mt-8">
            <TextField
              id={
                inspectionItemNameErrors
                  ? "outlined-error"
                  : "outlined-required"
              }
              label={`${t("itemName")}*`}
              className="input-field"
              value={localName}
              onChange={handleNameChange}
              onBlur={handleNameBlur}
              onFocus={handleNameFocus}
              error={!!inspectionItemNameErrors}
            />
            {inspectionItemNameErrors?.type && <IconInputError />}
          </div>
          {inspectionItemNameErrors?.type ? (
            <p className="txt-red fs-12 mt-0 mb-0 ml-15">
              {inspectionItemNameErrors?.type === "required" &&
                t("messages.require")}
              {inspectionItemNameErrors?.type === "hasInvalidChar" &&
                t("messages.validCharacters")}
              {inspectionItemNameErrors?.type === "hasEmoji" &&
                t("messages.validCharacters")}
            </p>
          ) : (
            <span className="fs-12 ml-15 txt-gray-1">&#42;{t("required")}</span>
          )}
          <div className="wrapper-text-field mt-24">
            <TextField
              multiline
              maxRows={3}
              className="input-field input-text-area"
              id={
                inspectionItemDescriptionErrors?.type
                  ? "outlined-error"
                  : "outlined-controlled"
              }
              label={t("explanation")}
              value={localDescription}
              onChange={handleDescriptionChange}
              onBlur={handleDescriptionBlur}
              onFocus={handleDescriptionFocus}
              error={
                !!inspectionItemDescriptionErrors &&
                inspectionItemDescriptionErrors?.type !== "maxGraphemes"
              }
            />
            {inspectionItemDescriptionErrors?.type &&
              inspectionItemDescriptionErrors?.type !== "maxGraphemes" && (
                <IconInputError />
              )}
          </div>
          {inspectionItemDescriptionErrors?.type && (
            <p className="txt-red fs-12 mt-0 mb-24 ml-15">
              {inspectionItemDescriptionErrors?.type === "hasInvalidChar" &&
                t("messages.validCharacters")}
              {inspectionItemDescriptionErrors?.type === "hasEmoji" &&
                t("messages.validCharacters")}
            </p>
          )}

          <div className="mt-16">
            {isMediaLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                }}
              >
                <CircularProgress size={40} sx={{ color: "#140a9a" }} />
              </div>
            ) : (
              <MediaUpload
                id={id}
                medias={mediaList}
                setMedias={handleMediaUpdate}
                typeFile={TYPE_FILE_UPLOAD.imageAndVideo}
                uploadDescription={uploadDescription}
                errMessage={mediaUploadErrorMsg}
              />
            )}
          </div>

          <Accordion
            className="mt-10"
            defaultExpanded={activeSwitch === 4 && !hasPredefinedComments}
            sx={{
              boxShadow: "none",
              borderBottom: "1px solid #ddd",
              borderTop: "1px solid #ddd",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
              className="card-bg"
              sx={{ backgroundColor: "#f5f6f9", marginRight: "-22px" }}
            >
              <Typography style={{ marginLeft: "-15px" }} component="span">
                {t("AdvancedSettings")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails
              className="card-bg"
              sx={{ backgroundColor: "#f5f6f9" }}
            >
              <div
                style={{ marginTop: "-25px", backgroundColor: "transparent" }}
              >
                <p className="txt-gray-3 mb-16" style={{ marginLeft: "-15px" }}>
                  {t("AnswerOptions")}
                </p>
                <div className="d-flex gap-24 justify-between flex-wrap answer-options-row">
                  {options.map((option) => (
                    <label
                      key={option.id}
                      className={`option-card ${
                        activeSwitch === option.id ? "selected" : ""
                      }`}
                    >
                      <div className="icons-row d-flex justify-content-center mb-8">
                        {option.icons?.map((Icon, idx) => (
                          <span key={idx} className="mx-5">
                            {Icon}
                          </span>
                        ))}
                      </div>
                      <div className="label text-center">{option.label}</div>
                      <input
                        type="radio"
                        name={`answerOption-${id}`}
                        checked={activeSwitch === option.id}
                        onChange={() => handleSwitchChange(option.id)}
                      />
                    </label>
                  ))}

                  <div
                    className={`option-card predefined-comment-option ${
                      activeSwitch === 4 ? "selected" : ""
                    }`}
                  >
                    <label className="predefined-comment-option-inner">
                      <div className="icons-row d-flex justify-content-center mb-8">
                        <IconMessage />
                      </div>
                      <div className="label text-center predefined-comment-option-label">
                        {t("predefinedCommentSelection")}
                      </div>
                      <input
                        type="radio"
                        name={`answerOption-${id}`}
                        checked={activeSwitch === 4}
                        onChange={() => handleSwitchChange(4)}
                      />
                    </label>

                    {activeSwitch === 4 && (
                      <button
                        type="button"
                        className={`predefined-comment-config-btn ${
                          hasPredefinedComments ? "has-items" : "no-items"
                        }`}
                        onClick={() => setOpenPredefinedDialog(true)}
                      >
                        <FormatListBulleted className="predefined-comment-config-icon" />
                        <span>{t("setPredefinedComments")}</span>
                        <span className="predefined-comment-config-count">
                          {t("predefinedCommentCount", {
                            count: predefinedCommentCount,
                            max: MAX_PREDEFINED_COMMENTS,
                          })}
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </AccordionDetails>
          </Accordion>

          <div
            className="d-flex justify-content-end mt-16"
            style={{ marginRight: "-20px", marginTop: "15px" }}
          >
            <Button
              className="btn-no-border-red btn-delete-card"
              onClick={() => setIsOpenDeleteDialog(true)}
            >
              <IconDelete />
              <span className="txt-red ml-10 fs-14">
                {t("formAction.delete")}
              </span>
            </Button>
          </div>
        </div>

        <ConfirmDialog
          open={isOpenDeleteDialog}
          setOpen={setIsOpenDeleteDialog}
          {...confirmProps}
        ></ConfirmDialog>

        <PredefinedCommentsDialog
          open={openPredefinedDialog}
          setOpen={setOpenPredefinedDialog}
          initialOptions={(card.options ?? []) as PredefinedCommentOption[]}
          onSave={handleSavePredefinedComments}
        />
      </li>
    );
  }),
);
